import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ResumeParserService {
  private apiUrl = environment.serverUrl;

  // Loading state management
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$ = this.loadingSubject.asObservable();

  // Progress tracking
  private progressSubject = new BehaviorSubject<number>(0);
  public progress$ = this.progressSubject.asObservable();

  constructor(private http: HttpClient) { }

  /**
   * Parse a resume file and extract structured data
   * @param file The resume file to parse (PDF, DOC, DOCX, TXT, JPG, or PNG)
   * @param candidateId Optional candidate ID
   * @param userId User ID (defaults from localStorage)
   * @param organisationId Optional organisation ID
   * @returns Observable with parsed resume data
   */
  parseResume(file: File, candidateId?: string, userId?: string, organisationId?: string): Observable<any> {
    // Start loading and reset progress
    this.loadingSubject.next(true);
    this.progressSubject.next(10);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', userId || localStorage.getItem('userId') || '');
    if (candidateId) {
      formData.append('candidateId', candidateId);
    }
    if (organisationId) {
      formData.append('organisationId', organisationId || localStorage.getItem('organisationId') || '');
    }

    const headers = new HttpHeaders({
      // 'Authorization': 'Bearer ' + localStorage.getItem('token') // If you use token auth
    });

    // Progress simulation during upload
    this.progressSubject.next(30);

    // Optimized with timeout handling and better error messages
    return this.http.post(`${this.apiUrl}candidates/parseResume`, formData, { headers })
      .pipe(
        timeout(90000), // Client timeout of 35 seconds
        map((response: any) => {
          this.progressSubject.next(70);

          // Quick validation to avoid processing invalid responses
          if (!response) {
            throw new Error('Empty response from server');
          }

          if (response && (response.code === '00000' || response.status === 'SUCCESS')) {
            this.progressSubject.next(90);

            // Optimized data extraction
            let resumeData = response.data?.resumeData || response.data;

            // Fast JSON parsing with error handling
            if (typeof resumeData === 'string') {
              try {
                resumeData = JSON.parse(resumeData);
              } catch (e) {
                console.error('Failed to parse resumeData as JSON:', e);
                throw new Error('Invalid resume data format received');
              }
            }

            this.progressSubject.next(100);
            this.loadingSubject.next(false);

            return {
              status: 'success',
              data: this.processResumeData(resumeData),
              fileName: response.data?.fileName,
              fileSize: response.data?.fileSize,
              processingTime: response.data?.processingTime
            };
          } else {
            throw new Error(response.message || 'Resume parsing failed on server');
          }
        }),
        catchError(error => {
          console.error('Resume parsing error:', error);
          this.loadingSubject.next(false);
          this.progressSubject.next(0);

          // Better error categorization for user feedback
          let errorMessage = 'An error occurred while parsing the resume';

          if (error.name === 'TimeoutError' || error.message?.includes('timeout')) {
            errorMessage = 'Resume parsing timed out. Please try with a smaller file or try again later.';
          } else if (error.status === 413) {
            errorMessage = 'File is too large. Please upload a file smaller than 2MB.';
          } else if (error.status === 415) {
            errorMessage = 'Unsupported file format. Please upload PDF, DOC, DOCX, TXT, JPG, or PNG files only.';
          } else if (error.status === 0) {
            errorMessage = 'Connection failed. Please check your internet connection and try again.';
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          return of({
            status: 'error',
            message: errorMessage,
            errorCode: error.status,
            timestamp: new Date().toISOString()
          });
        })
      );
  }

  /**
   * Process and normalize the parsed resume data
   * @param data Raw data from the resume parser API
   * @returns Normalized resume data
   */
  private processResumeData(data: any): any {
    // If data is a string, try to parse it as JSON
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        console.error('Failed to parse resume data as JSON:', e);
        return data;
      }
    }

    // Handle the nested resumeData structure from the API
    if (data && data.resumeData) {
      data = data.resumeData;
    }

    // Fix location parsing: detect when "state" is actually a country code
    // e.g., "Coimbatore, IN 641671" where "IN" is India country code, not a state
    const locationData = this.fixLocationParsing(data.personal_info);

    // Normalize the data structure while preserving all fields from ChatGPT
    // Use null instead of empty strings to trigger validation errors for missing fields
    return {
      personal_info: {
        name: data.personal_info?.name || null,
        email: data.personal_info?.email || null,
        phone: data.personal_info?.phone || null,
        location: data.personal_info?.location || null,
        city: locationData.city,
        state: locationData.state,
        country: locationData.country,
        zipCode: locationData.zipCode,
        linkedin: data.personal_info?.linkedin || null,
        portfolio: data.personal_info?.portfolio || null,
        targetRateFrom: data.personal_info?.targetRateFrom || null,
        targetRateTo: data.personal_info?.targetRateTo || null,
        payType: data.personal_info?.payType || null
      },
      summary: data.summary || '',
      experience: data.experience || [],
      education: data.education || [],
      skills: {
        technical_skills: data.skills?.technical_skills || [],
        languages: data.skills?.languages || [],
        frameworks: data.skills?.frameworks || [],
        tools: data.skills?.tools || [],
        databases: data.skills?.databases || [],
        soft_skills: data.skills?.soft_skills || [],
        primary_skills: data.skills?.primary_skills || [],
        secondary_skills: data.skills?.secondary_skills || []
      },
      projects: data.projects || [],
      certifications: data.certifications || [],
      languages: data.languages || [],
      awards: data.awards || [],
      // IMPORTANT: Preserve ChatGPT's skill analysis and other advanced fields
      skill_analysis: data.skill_analysis || null,
      ranking_metrics: data.ranking_metrics || null,
      job_title_match: data.job_title_match || null,
      strengths: data.strengths || [],
      gaps: data.gaps || [],
      target_job_title: data.target_job_title || null
    };
  }

  /**
   * Fix location parsing issues where "state" is actually a country code
   * e.g., "Coimbatore, IN 641671" where AI incorrectly parses "IN" as state
   * @param personalInfo The personal_info object from parsed resume
   * @returns Corrected location fields
   */
  private fixLocationParsing(personalInfo: any): { city: string | null; state: string | null; country: string | null; zipCode: string | null } {
    if (!personalInfo) {
      return { city: null, state: null, country: null, zipCode: null };
    }

    let city = personalInfo.city || null;
    let state = personalInfo.state || null;
    let country = personalInfo.country || null;
    let zipCode = personalInfo.zipCode || personalInfo.zipcode || null;

    // Country codes that might be incorrectly parsed as state
    // Note: Removed US and CA as they are valid US state codes (e.g., CA = California)
    const countryCodesOnly: { [key: string]: string } = {
      'IN': 'IN',   // India
      'AU': 'AU',   // Australia
      'GB': 'GB',   // United Kingdom
      'UK': 'GB',   // United Kingdom
    };

    // Check if "state" is actually a country code (only if country is not already set)
    const stateUpper = state?.toUpperCase?.();
    if (stateUpper && countryCodesOnly[stateUpper] && !country) {
      console.log(`Detected state "${state}" is actually country code, correcting...`);

      // Use state value as country
      country = countryCodesOnly[stateUpper];

      // Clear state since it was actually a country code
      state = null;
    }

    // Also try to extract zipCode from location string if not found
    // e.g., "Coimbatore, IN 641671" -> zipCode = "641671"
    if (!zipCode && personalInfo.location) {
      const zipMatch = personalInfo.location.match(/\b(\d{5,6})\b/);
      if (zipMatch) {
        zipCode = zipMatch[1];
        console.log(`Extracted zipCode from location string: ${zipCode}`);
      }
    }

    return { city, state, country, zipCode };
  }

  /**
   * Validate if a file is a valid resume format
   * @param file The file to validate
   * @returns Boolean indicating if the file is valid
   */
  validateResumeFile(file: File): { valid: boolean; message?: string } {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      // Image resumes (photo / scan) - transcribed server-side via OCR
      'image/jpeg',
      'image/png'
    ];

    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        message: 'Please upload PDF, DOC, DOCX, TXT, JPG, or PNG files only'
      };
    }

    if (file.size > maxSize) {
      return {
        valid: false,
        message: 'File size must be less than 10MB'
      };
    }

    return { valid: true };
  }

  /**
   * Generate an enhanced summary based on parsed resume data
   * This builds a comprehensive professional profile from all extracted information
   * @param parsedData The parsed resume data from API
   * @returns Enhanced, detailed summary
   */
  generateEnhancedSummary(parsedData: any): string {
    if (!parsedData) return '';
    
    let enhancedSummary = '';
    
    // Start with the AI-generated summary from the server
    if (parsedData.summary) {
      enhancedSummary = parsedData.summary;
    }
    
    // Calculate total experience for enrichment
    const totalYears = this.calculateTotalExperience(parsedData.experience || []);
    
    // Build a comprehensive professional overview
    if (parsedData.experience && parsedData.experience.length > 0) {
      const latestRole = parsedData.experience[0];
      const companies = [...new Set(parsedData.experience.map(exp => exp.company))];
      
      // Add professional journey context if not in summary
      if (!enhancedSummary.toLowerCase().includes('experience')) {
        enhancedSummary += ` ${totalYears}+ years of progressive experience across ${companies.length} organization${companies.length > 1 ? 's' : ''}.`;
      }
      
      // Highlight key achievements from all roles
      const allAchievements = parsedData.experience
        .flatMap(exp => exp.achievements || [])
        .filter(ach => ach);
      
      if (allAchievements.length > 0 && !enhancedSummary.toLowerCase().includes('achievement')) {
        enhancedSummary += ` Notable achievements: ${allAchievements.slice(0, 3).join('; ')}.`;
      }
      
      // Add responsibilities overview
      const keyResponsibilities = parsedData.experience
        .flatMap(exp => exp.responsibilities || [])
        .filter((resp, index, self) => resp && self.indexOf(resp) === index)
        .slice(0, 5);
      
      if (keyResponsibilities.length > 0 && !enhancedSummary.toLowerCase().includes('responsibilit')) {
        enhancedSummary += ` Core responsibilities encompass: ${keyResponsibilities.join(', ')}.`;
      }
    }
    
    // Enrich with comprehensive skills if not already included
    if (parsedData.skills && !enhancedSummary.toLowerCase().includes('skill')) {
      const allSkills = [
        ...(parsedData.skills.technical_skills || []),
        ...(parsedData.skills.frameworks || []),
        ...(parsedData.skills.tools || []),
        ...(parsedData.skills.databases || [])
      ].filter((skill, index, self) => skill && self.indexOf(skill) === index);
      
      if (allSkills.length > 0) {
        enhancedSummary += ` Technical proficiencies: ${allSkills.slice(0, 15).join(', ')}.`;
      }
      
      // Add soft skills if available
      if (parsedData.skills.soft_skills && parsedData.skills.soft_skills.length > 0) {
        enhancedSummary += ` Soft skills: ${parsedData.skills.soft_skills.slice(0, 5).join(', ')}.`;
      }
    }
    
    // Add education and certifications for completeness
    if (parsedData.education && parsedData.education.length > 0 && !enhancedSummary.toLowerCase().includes('education')) {
      const degrees = parsedData.education
        .map(edu => `${edu.degree} in ${edu.field_of_study}`)
        .filter(deg => deg);
      
      if (degrees.length > 0) {
        enhancedSummary += ` Educational qualifications: ${degrees.join(', ')}.`;
      }
    }
    
    // Include certifications
    if (parsedData.certifications && parsedData.certifications.length > 0 && !enhancedSummary.toLowerCase().includes('certif')) {
      const certNames = parsedData.certifications
        .map(cert => typeof cert === 'object' ? cert.name : cert)
        .filter(name => name);
      
      if (certNames.length > 0) {
        enhancedSummary += ` Certifications: ${certNames.join(', ')}.`;
      }
    }
    
    // Add awards if present
    if (parsedData.awards && parsedData.awards.length > 0) {
      enhancedSummary += ` Awards & Recognition: ${parsedData.awards.join(', ')}.`;
    }
    
    // Clean up the summary
    enhancedSummary = enhancedSummary
      .replace(/\s+/g, ' ')  // Remove extra spaces
      .replace(/\.\s*\./g, '.')  // Remove double periods
      .trim();
    
    return enhancedSummary;
  }

  /**
   * Extract names from a full name string
   * @param fullName The full name string
   * @returns Object with firstName and lastName
   */
  extractNames(fullName: string): { firstName: string; lastName: string } {
    if (!fullName) return { firstName: '', lastName: '' };
    
    const parts = fullName.trim().split(' ');
    return {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || ''
    };
  }

  /**
   * Calculate total experience from experience array
   * @param experiences Array of experience objects
   * @returns Total years of experience
   */
  calculateTotalExperience(experiences: any[]): number {
    if (!experiences || experiences.length === 0) return 0;
    
    let totalMonths = 0;
    experiences.forEach(exp => {
      if (exp.start_date) {
        const startDate = new Date(exp.start_date);
        const endDate = exp.end_date && exp.end_date !== 'Present' 
          ? new Date(exp.end_date) 
          : new Date();
        
        const months = (endDate.getFullYear() - startDate.getFullYear()) * 12 
          + (endDate.getMonth() - startDate.getMonth());
        totalMonths += months;
      }
    });
    
    return Math.round(totalMonths / 12);
  }
}